/**
 * Bulk Upload — Relation Resolver
 *
 * Scores raw cell values against lookup data to resolve relational references.
 * Uses a 4-tier matching strategy: exact → token → prefix-aware → Jaro-Winkler.
 */

import {
	MatchConfidence,
	RelationScoreResult,
	ResolutionGroup,
	ScoredCandidate,
	UploadFieldDef,
	LookupItem,
	ContextDefault,
	ContextRow,
	UploadSchema,
	LookupData,
} from './types';

// ---------------------------------------------------------------------------
// String Utilities
// ---------------------------------------------------------------------------

/** Normalize: lowercase, strip diacritics, collapse whitespace, remove punctuation */
export function normalize(s: string): string {
	return s
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '') // strip diacritics
		.replace(/[^\w\s]/g, ' ') // punctuation → space
		.replace(/\s+/g, ' ') // collapse whitespace
		.trim();
}

/** Tokenize into sorted unique words */
export function tokenize(s: string): string[] {
	return [...new Set(s.split(' ').filter(Boolean))].sort();
}

/** Overlap score: how many tokens match / max(token count) */
function tokenOverlapScore(a: string[], b: string[]): number {
	if (a.length === 0 || b.length === 0) return 0;
	const matches = a.filter((t) => b.includes(t)).length;
	return matches / Math.max(a.length, b.length);
}

/**
 * Prefix-aware matching: "J" matches "John" because it's a prefix.
 * Handles abbreviations like "J. Smith" → "John Smith",
 * "Min. of Edu." → "Ministry of Education"
 */
function prefixAwareScore(rawTokens: string[], labelTokens: string[]): number {
	if (rawTokens.length === 0 || labelTokens.length === 0) return 0;

	let matches = 0;
	const used = new Set<number>();

	for (const rt of rawTokens) {
		const idx = labelTokens.findIndex(
			(lt, i) => !used.has(i) && (lt === rt || lt.startsWith(rt) || rt.startsWith(lt))
		);
		if (idx !== -1) {
			matches++;
			used.add(idx);
		}
	}

	return matches / Math.max(rawTokens.length, labelTokens.length);
}

/**
 * Jaro-Winkler similarity (0–1).
 * Better than Levenshtein for names — rewards common prefixes.
 */
export function jaroWinkler(s1: string, s2: string): number {
	if (s1 === s2) return 1;
	if (s1.length === 0 || s2.length === 0) return 0;

	const matchWindow = Math.max(Math.floor(Math.max(s1.length, s2.length) / 2) - 1, 0);

	const s1Matches = new Array(s1.length).fill(false);
	const s2Matches = new Array(s2.length).fill(false);

	let matches = 0;
	let transpositions = 0;

	// Find matches
	for (let i = 0; i < s1.length; i++) {
		const start = Math.max(0, i - matchWindow);
		const end = Math.min(i + matchWindow + 1, s2.length);

		for (let j = start; j < end; j++) {
			if (s2Matches[j] || s1[i] !== s2[j]) continue;
			s1Matches[i] = true;
			s2Matches[j] = true;
			matches++;
			break;
		}
	}

	if (matches === 0) return 0;

	// Count transpositions
	let k = 0;
	for (let i = 0; i < s1.length; i++) {
		if (!s1Matches[i]) continue;
		while (!s2Matches[k]) k++;
		if (s1[i] !== s2[k]) transpositions++;
		k++;
	}

	const jaro =
		(matches / s1.length + matches / s2.length + (matches - transpositions / 2) / matches) / 3;

	// Winkler modification: boost for common prefix (up to 4 chars)
	let prefix = 0;
	for (let i = 0; i < Math.min(4, Math.min(s1.length, s2.length)); i++) {
		if (s1[i] === s2[i]) {
			prefix++;
		} else {
			break;
		}
	}

	return jaro + prefix * 0.1 * (1 - jaro);
}

// ---------------------------------------------------------------------------
// Core Relation Scoring
// ---------------------------------------------------------------------------

/**
 * Score a raw spreadsheet value against a list of lookup items.
 * Returns the best match, confidence level, and ranked candidates.
 *
 * Only exact matches (tier 1) auto-resolve. Everything else returns candidates
 * for the interactive resolution screen.
 */
export function scoreRelation(
	rawValue: string,
	field: UploadFieldDef,
	lookupItems: LookupItem[]
): RelationScoreResult {
	const relation = field.relation;
	if (!relation) {
		return { resolvedId: null, confidence: 'none', candidates: [] };
	}

	const raw = rawValue.trim();
	if (!raw) {
		return { resolvedId: null, confidence: 'none', candidates: [] };
	}

	const rawNorm = normalize(raw);

	// --- Tier 1: Deterministic matching (exact match → auto-resolve) ---
	for (const item of lookupItems) {
		const id = item[relation.idField] as number;
		const label = String(item[relation.matchField] ?? '');
		const labelNorm = normalize(label);

		// Exact normalized string match
		if (rawNorm === labelNorm) {
			return {
				resolvedId: id,
				confidence: 'exact',
				candidates: [{ id, label, score: 1.0, matchType: 'exact' }],
			};
		}

		// Direct ID match (if user typed a number)
		if (/^\d+$/.test(raw) && id === parseInt(raw, 10)) {
			return {
				resolvedId: id,
				confidence: 'exact',
				candidates: [{ id, label, score: 1.0, matchType: 'exact' }],
			};
		}

		// Match on alternate fields (idExternal, code, etc.)
		if (relation.alternateMatchFields) {
			for (const altField of relation.alternateMatchFields) {
				const altValue = item[altField];
				if (altValue && normalize(String(altValue)) === rawNorm) {
					return {
						resolvedId: id,
						confidence: 'exact',
						candidates: [{ id, label, score: 1.0, matchType: 'exact' }],
					};
				}
			}
		}
	}

	// --- Tiers 2-4: Fuzzy matching (all return candidates, never auto-resolve) ---
	const scored: ScoredCandidate[] = [];
	const rawTokens = tokenize(rawNorm);

	for (const item of lookupItems) {
		const id = item[relation.idField] as number;
		const label = String(item[relation.matchField] ?? '');
		const labelNorm = normalize(label);
		const labelTokens = tokenize(labelNorm);

		let bestScore = 0;
		let bestType: ScoredCandidate['matchType'] = 'fuzzy';

		// Tier 2: Token-based overlap
		const tScore = tokenOverlapScore(rawTokens, labelTokens);
		if (tScore > bestScore) {
			bestScore = tScore;
			bestType = 'token';
		}

		// Tier 3: Prefix-aware matching
		const pScore = prefixAwareScore(rawTokens, labelTokens);
		if (pScore > bestScore) {
			bestScore = pScore;
			bestType = 'prefix';
		}

		// Tier 4: Jaro-Winkler fuzzy
		const jwScore = jaroWinkler(rawNorm, labelNorm);
		if (jwScore > bestScore) {
			bestScore = jwScore;
			bestType = 'fuzzy';
		}

		if (bestScore > 0.5) {
			scored.push({ id, label, score: bestScore, matchType: bestType });
		}
	}

	// Sort by score descending
	scored.sort((a, b) => b.score - a.score);

	if (scored.length === 0) {
		return { resolvedId: null, confidence: 'none', candidates: [] };
	}

	// Determine confidence based on top score and gap to runner-up
	const top = scored[0];
	const gap = scored.length > 1 ? top.score - scored[1].score : 1;
	let confidence: MatchConfidence;

	if (top.score >= 0.95 && gap >= 0.1) {
		confidence = 'high'; // Auto-suggested but shown on resolution screen
	} else if (top.score >= 0.8) {
		confidence = 'medium'; // Needs user confirmation
	} else {
		confidence = 'low'; // User must search/pick
	}

	return {
		resolvedId: confidence === 'high' ? top.id : null,
		confidence,
		candidates: scored.slice(0, 8),
	};
}

// ---------------------------------------------------------------------------
// Resolution Grouping
// ---------------------------------------------------------------------------

/**
 * Build resolution groups by grouping all rows that share the same raw value
 * per relation field. This ensures "J. Smith" appearing in 30 rows is resolved once.
 */
export function buildResolutionGroups(
	rows: Record<string, unknown>[],
	fields: UploadFieldDef[],
	mappings: Array<{ targetField: string; sourceColumn: string | null }>,
	lookupData: LookupData
): ResolutionGroup[] {
	const groups = new Map<string, ResolutionGroup>();

	const relationFields = fields.filter((f) => f.dataType === 'relation' && f.relation);

	for (const field of relationFields) {
		const mapping = mappings.find((m) => m.targetField === field.name);
		if (!mapping?.sourceColumn) continue;

		const items = (field.relation && lookupData[field.relation.contextKey]) ?? [];

		for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
			const rawValue = String(rows[rowIdx][mapping.sourceColumn] ?? '').trim();
			if (!rawValue) continue;

			const key = `${field.name}::${normalize(rawValue)}`;

			if (groups.has(key)) {
				const group = groups.get(key);
				if (!group) continue;
				group.rowCount++;
				group.rowIndices.push(rowIdx);
			} else {
				const scoreResult = scoreRelation(rawValue, field, items);
				groups.set(key, {
					key,
					field,
					rawValue,
					scoreResult,
					rowCount: 1,
					rowIndices: [rowIdx],
					resolution: {
						status: scoreResult.confidence === 'exact' ? 'accepted' : 'pending',
						selectedId: scoreResult.resolvedId,
						selectedLabel: scoreResult.candidates[0]?.label ?? null,
					},
				});
			}
		}
	}

	// Sort: pending groups first, then by row count descending
	return Array.from(groups.values()).sort((a, b) => {
		if (a.resolution.status === 'pending' && b.resolution.status !== 'pending') return -1;
		if (a.resolution.status !== 'pending' && b.resolution.status === 'pending') return 1;
		return b.rowCount - a.rowCount;
	});
}

// ---------------------------------------------------------------------------
// Context Row Relation Resolution
// ---------------------------------------------------------------------------

/**
 * Resolve context row defaults, including relational values.
 * Primitive values pass through; relation values run through scoreRelation().
 */
export function resolveContextDefaults(
	contextRows: ContextRow[],
	schema: UploadSchema,
	lookupData: LookupData
): ContextDefault[] {
	const defaults: ContextDefault[] = [];

	for (const ctx of contextRows) {
		for (const entry of ctx.entries) {
			// Match the label to a schema field
			const field = schema.fields.find(
				(f) =>
					normalize(f.label) === normalize(entry.label) ||
					(f.headerAliases ?? []).some((a) => normalize(a) === normalize(entry.label))
			);
			if (!field) continue;

			const def: ContextDefault = {
				label: entry.label,
				targetField: field.name,
				rawValue: entry.value,
				resolvedValue: entry.value,
				confirmed: false,
			};

			if (field.dataType === 'relation' && field.relation) {
				const items = lookupData[field.relation.contextKey] ?? [];
				const result = scoreRelation(entry.value, field, items);
				def.relation = {
					resolvedId: result.resolvedId,
					resolvedLabel: result.candidates[0]?.label ?? null,
					confidence: result.confidence,
					candidates: result.candidates,
				};
				def.resolvedValue = result.resolvedId;
			} else if (field.transform) {
				def.resolvedValue = field.transform(entry.value);
			}

			defaults.push(def);
		}
	}

	return defaults;
}

// ---------------------------------------------------------------------------
// Apply Resolutions to Validation Results
// ---------------------------------------------------------------------------

/**
 * After the user resolves relations on the resolution screen,
 * apply their decisions back into the validation results.
 */
export function applyResolutions(
	validationResults: Array<{
		rowIndex: number;
		status: string;
		errors: Array<{ field: string; message: string; value: unknown }>;
		warnings: Array<{ field: string; message: string; value: unknown }>;
		resolvedRow: Record<string, unknown>;
	}>,
	resolutionGroups: ResolutionGroup[]
): void {
	for (const group of resolutionGroups) {
		const { resolution, field, rowIndices } = group;

		if (
			resolution.status === 'accepted' ||
			resolution.status === 'manual' ||
			resolution.status === 'created'
		) {
			for (const rowIdx of rowIndices) {
				const result = validationResults[rowIdx];
				if (!result) continue;

				result.resolvedRow[field.name] = resolution.selectedId;
				result.errors = result.errors.filter((e) => e.field !== field.name);
			}
		} else if (resolution.status === 'skipped') {
			for (const rowIdx of rowIndices) {
				const result = validationResults[rowIdx];
				if (!result) continue;
				result.status = 'skipped';
			}
		}
	}

	// Recompute row statuses
	for (const r of validationResults) {
		if (r.status === 'skipped') continue;
		r.status = r.errors.length > 0 ? 'error' : r.warnings.length > 0 ? 'warning' : 'valid';
	}
}

// ---------------------------------------------------------------------------
// Search Lookup (for the resolution screen's search box)
// ---------------------------------------------------------------------------

/**
 * Client-side search across lookup data for the resolution screen.
 * Matches against the primary matchField, ID, and alternate fields.
 */
export function searchLookup(
	query: string,
	items: LookupItem[],
	matchField: string,
	alternateMatchFields?: string[]
): ScoredCandidate[] {
	const q = normalize(query);
	if (!q) return [];

	const allFields = [matchField, 'id', ...(alternateMatchFields ?? [])];

	return items
		.map((item) => {
			const label = String(item[matchField] ?? '');
			const searchable = allFields.map((f) => normalize(String(item[f] ?? '')));

			const score = Math.max(
				...searchable.map((s) => {
					if (!s) return 0;
					if (s.includes(q)) return 0.9 + (q.length / s.length) * 0.1;
					return jaroWinkler(q, s);
				})
			);

			return { id: item.id, label, score, matchType: 'fuzzy' as const };
		})
		.filter((c) => c.score > 0.4)
		.sort((a, b) => b.score - a.score)
		.slice(0, 15);
}
